import { useState } from "react";
import { CheckCircle2, Circle, Upload, Link2, Save, FileDown } from "lucide-react";
import { toast } from "sonner";
import { API, api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { DocRow } from "@/components/realestate/DocRow";
import { ContactForm } from "@/components/realestate/ContactForm";
import { ItemTaskRow } from "@/components/realestate/ItemTaskRow";

const UploadButton = ({ property, itemKey, multiple, onChanged, label }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        form.append("entity_type", "property");
        form.append("entity_id", property.id);
        form.append("tags", itemKey);
        await api.post(`/workspaces/${property.workspace_id}/files`, form);
      }
      toast.success(files.length === 1 ? "Documento subido" : `${files.length} documentos subidos`);
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline cursor-pointer" data-testid={`upload-${itemKey}`}>
      <Upload size={13} /> {busy ? "Subiendo…" : label}
      <input type="file" multiple={multiple} className="hidden" onChange={upload} disabled={busy} />
    </label>
  );
};

const TextValue = ({ property, item, onChanged }) => {
  const saved = property.items?.[item.key]?.value || "";
  const [value, setValue] = useState(saved);
  const save = async () => {
    try {
      await api.put(`/properties/${property.id}/items/${item.key}`, { value });
      toast.success("Guardado");
      onChanged();
    } catch (err) { toast.error(apiError(err)); }
  };
  const dirty = value !== saved;
  return (
    <div className="space-y-2">
      {item.type === "link" ? (
        <div className="flex gap-2">
          <Input data-testid={`item-input-${item.key}`} value={value} onChange={(e) => setValue(e.target.value)} placeholder="https://my.matterport.com/show/…" className="h-8 text-xs" />
          {saved && <Button asChild size="sm" variant="outline" className="h-8"><a href={saved} target="_blank" rel="noreferrer"><Link2 size={13} /></a></Button>}
        </div>
      ) : (
        <Textarea data-testid={`item-input-${item.key}`} value={value} onChange={(e) => setValue(e.target.value)} rows={3} className="text-xs" placeholder={item.hint || "Escribe aquí…"} />
      )}
      {dirty && <Button size="sm" className="h-7 text-xs" onClick={save} data-testid={`item-save-${item.key}`}><Save size={12} className="mr-1" /> Guardar</Button>}
    </div>
  );
};

export const ChecklistItem = ({ property, item, done, files, onChanged, canWrite, members, children }) => {
  const isDocs = item.type === "docs";
  const tplKey = item.key === "autorizacion_cee_2" ? "autorizacion_cee_1" : item.key;
  const templates = (property.doc_templates || []).filter((t) => (t.tags || []).includes(tplKey));
  return (
    <div className={`rounded-xl border p-3 space-y-2 transition-colors ${done ? "border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/10" : item.required ? "border-border bg-card" : "border-dashed bg-card/60"}`}
      data-testid={`checklist-item-${item.key}`} data-done={done ? "1" : "0"}>
      <div className="flex items-start gap-2">
        {done ? <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" /> : <Circle size={18} className="text-muted-foreground/50 shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="text-sm font-medium leading-tight">{item.label}</p>
            {item.required
              ? <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-amber-300 text-amber-700">Obligatorio</Badge>
              : <Badge variant="outline" className="text-[10px] h-4 px-1.5 text-muted-foreground">Opcional</Badge>}
          </div>
          {item.hint && <p className="text-[11px] text-muted-foreground">{item.hint}</p>}
          {templates.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1" data-testid={`item-templates-${item.key}`}>
              {templates.map((t) => (
                <a key={t.id} href={`${API}/files/${t.id}/download`} download={t.name} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
                  <FileDown size={11} /> Plantilla: {t.name}
                </a>
              ))}
            </div>
          )}
        </div>
        {canWrite && (item.type === "doc" || isDocs) && (
          <UploadButton property={property} itemKey={item.key} multiple={isDocs} onChanged={onChanged}
            label={isDocs ? "Añadir" : files.length ? "Subir otra versión" : "Subir"} />
        )}
      </div>
      <div className="pl-6"><ItemTaskRow task={property.tasks_by_item?.[item.key]} members={members} canWrite={canWrite} onChanged={onChanged} itemKey={item.key} /></div>
      {(item.type === "doc" || isDocs) && files.length > 0 && (
        <div className="space-y-1 pl-6">{files.map((f) => <DocRow key={f.id} file={f} onChanged={onChanged} canWrite={canWrite} />)}</div>
      )}
      {(item.type === "text" || item.type === "link") && (
        <div className="pl-6">{canWrite ? <TextValue key={property.items?.[item.key]?.value || ""} property={property} item={item} onChanged={onChanged} /> : <p className="text-xs whitespace-pre-wrap">{property.items?.[item.key]?.value || "—"}</p>}</div>
      )}
      {item.type === "form" && (
        <div className="pl-6"><ContactForm property={property} who={item.key === "datos_vendedor" ? "seller" : "buyer"} onChanged={onChanged} canWrite={canWrite} /></div>
      )}
      {children && <div className="pl-6">{children}</div>}
    </div>
  );
};
